import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Auth
export const auth = getAuth(app);

// Initialize Firestore with settings to prevent timestamp errors
initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  experimentalForceLongPolling: true, // Add this to ensure better compatibility
});

// Get Firestore instance
export const db = getFirestore(app);

// Local demo mode: Auth + Firestore on the emulators (`firebase emulators:start
// --only auth,firestore`), so nothing touches production data. Off unless set.
// VITE_EMULATOR_HOST: use a hostname the browser has no cookies for (e.g.
// devotionfund-api.localhost). A browser that has run many local apps carries
// 10-20 KB of cookies for 127.0.0.1/localhost, and the emulators reject that
// with 431 — which surfaced as every church page "not on the map".
const EMULATOR_HOST = import.meta.env?.VITE_EMULATOR_HOST || '127.0.0.1';
if (import.meta.env?.VITE_USE_DATA_EMULATOR === 'true') {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, EMULATOR_HOST, 8085);
  console.log('[Firebase] Auth + Firestore → LOCAL EMULATOR');
}

// Initialize and export Functions
// Use explicit region to match deployed Cloud Functions
export const functions = getFunctions(app, 'us-central1');

// Connect to Functions emulator when VITE_USE_EMULATOR is set (local dev only).
// Firestore stays on production so you work with real data.
if (import.meta.env?.VITE_USE_EMULATOR === 'true') {
  try {
    connectFunctionsEmulator(functions, EMULATOR_HOST, 5001);
    console.log('[Firebase] Connected to Functions emulator at 127.0.0.1:5001');
  } catch {
    console.log('[Firebase] Functions emulator already connected');
  }
}

if (import.meta.env?.MODE !== 'production') {
  console.log('[Firebase] Using project:', firebaseConfig.projectId);
  if (import.meta.env?.VITE_USE_EMULATOR === 'true') {
    console.log('[Firebase] Functions → LOCAL EMULATOR (test Stripe keys)');
  } else {
    console.log('[Firebase] Functions → PRODUCTION (live Stripe keys)');
  }
}